import { DatePicker } from '@components/primitives/DatePicker'
import { Icon } from '@components/primitives/Icon'
import Input from '@components/primitives/Input'
import { Text } from '@components/primitives/Text'
import { faPlayCircle } from '@fortawesome/pro-regular-svg-icons/faPlayCircle'
import { trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import { utcDateStringMMDDYYYY } from '@lib/utilities/date/utcDateString'
import {
  RELEASE_DETAILS_DOB_INPUT_NAME,
  RELEASE_DETAILS_FULL_NAME_INPUT_NAME,
} from '@modules/patientTrialApplications/postApplyFlow/consts'
import type { ApplySuccessPageProps } from 'app/p/apply-success/[token]/[step]/page'
import debounce from 'lodash/debounce'
import { useState } from 'react'
import { HipaaAndSoc2BadgeBox } from './HipaaAndSoc2BadgeBox'
import { MedicalReleaseAddressInputs } from './MedicalReleaseAddressInputs'
import { WhyShouldIProvideMyMedicalRecordsModal } from './WhyShouldIProvideMyMedicalRecordsModal'

export type InputNames =
  | 'dob'
  | 'addressLine1'
  | 'city'
  | 'state'
  | 'country'
  | 'zipCode'
  | 'name'
  | 'googlePlace'

export function MedicalReleaseDetailsPrompt({
  idToken,
  patientAddress,
  patientDateOfBirth,
  patientName: patientNameFromProps,
}: Pick<
  ApplySuccessPageProps,
  'idToken' | 'patientAddress' | 'patientDateOfBirth' | 'patientName'
>) {
  const [isModalOpen, setModalOpen] = useState(false)
  const dobString = patientDateOfBirth
    ? utcDateStringMMDDYYYY(new Date(patientDateOfBirth))
    : ''

  const [dateOfBirth, setDateOfBirth] = useState<string>(dobString)
  const [patientName, setPatientName] = useState<string>(
    patientNameFromProps ?? '',
  )

  const debouncedInputTracking = debounce((type: InputNames, val: string) => {
    trackEvent(TrackingEvents.EMR_RELEASE_INPUT_MODIFIED, {
      trialApplicationIdToken: idToken,
      type,
      val,
    })
  })

  return (
    <>
      <WhyShouldIProvideMyMedicalRecordsModal
        isModalOpen={isModalOpen}
        setModalOpen={setModalOpen}
      />
      <div className='space-y-4'>
        <Text element='h1' styleName='h3'>
          {`Next, let's request medical records on your behalf`}
        </Text>
        <Text>
          Some clinics choose only to review applications from patients who
          share their medical records.
        </Text>
        <>
          <div className='space-y-4'>
            {/* if we already have the patient name, don't bother trying to collect it */}
            <div className={patientName ? 'hidden' : ''}>
              <Text styleName='p-tiny' value='Full Legal Name' />
              <Input
                autoComplete='name'
                className='h-12 rounded-md bg-white'
                maxLength={128}
                name={RELEASE_DETAILS_FULL_NAME_INPUT_NAME}
                onChange={(event) => {
                  setPatientName(event.target.value)
                  debouncedInputTracking('name', event.target.value)
                }}
                placeholder='John Doe'
                required={true}
                value={patientName}
              />
            </div>
            <div className={patientDateOfBirth ? 'hidden' : ''}>
              <input
                name={RELEASE_DETAILS_DOB_INPUT_NAME}
                type='hidden'
                value={dateOfBirth}
              />
              <Text styleName='p-tiny' value='Date Of Birth' />
              <DatePicker
                className='h-12'
                id={`dob-${idToken}`}
                initialValue={dateOfBirth}
                onChange={(dob) => {
                  const dobString = dob
                    ? utcDateStringMMDDYYYY(new Date(dob))
                    : ''
                  setDateOfBirth(dobString)
                  debouncedInputTracking('dob', dobString)
                }}
                required={true}
              />
            </div>
            <MedicalReleaseAddressInputs
              idToken={idToken}
              patientAddress={patientAddress}
            />
          </div>
        </>
        <Text
          className='cursor-pointer text-neutral500 underline'
          onClick={() => {
            trackEvent(TrackingEvents.EMR_RELEASE_VIDEO_OPENED)
            setModalOpen(true)
          }}
          styleName='p-small'
        >
          <Icon className='mr-2' icon={faPlayCircle} />
          Why do you need this?
        </Text>
        <HipaaAndSoc2BadgeBox
          onClick={() => {
            trackEvent(TrackingEvents.EMR_RELEASE_VIDEO_OPENED)
            setModalOpen(true)
          }}
        />
      </div>
    </>
  )
}
